import { AxiosError } from "axios";
import { Button } from "@mui/material";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

import { ApiError, UploadResult, Vendor } from "../../models";
import { uploadFile } from "../../api/generic-api";

interface UseUploadVendorsOptions {
  onUploadResult?: (result: UploadResult<Vendor>) => void;
  onOpenDetails?: () => void;
}

export const useUploadVendors = (options?: UseUploadVendorsOptions) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  return useMutation<UploadResult<Vendor>, AxiosError<ApiError>, File>(
    (file: File) =>
      uploadFile<{ file: File }, UploadResult<Vendor>>(`/vendors/upload`, {
        file,
      }),
    {
      onSuccess: (result) => {
        options?.onUploadResult?.(result);

        const { createdCount, updatedCount, skippedCount, rowErrors } = result;
        const errorCount = rowErrors?.length ?? 0;
        const summary = `Upload complete: ${createdCount} created, ${updatedCount} updated, ${skippedCount} skipped${
          errorCount ? `, ${errorCount} with issues` : ""
        }.`;

        enqueueSnackbar(summary, {
          variant: errorCount ? "warning" : "success",
          action:
            options?.onOpenDetails &&
            ((key) => (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  options.onOpenDetails?.();
                  closeSnackbar(key);
                }}
              >
                View details
              </Button>
            )),
        });
      },
      onError: (error) => {
        const message =
          error.response?.data?.message ?? "Vendors Uploading Error";
        enqueueSnackbar(message, { variant: "error" });
      },
    }
  );
};
